import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractDynamicComponent } from '@components/abstracts';
import { FormulaireCatalogue, FormValid } from '@components/formulaire-catalogue/models';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button';
import { CheckboxComponent } from '@shared/components/checkbox';
import { ImageComponent } from '@shared/components/image';
import { TitleComponent } from '@shared/components/title';
import { HandleHtmlLinksDirective, ViewportObserverDirective } from '@shared/directives';
import { CatalogueService } from '@shared/services/catalogue';
import { Catalogue } from '@shared/services/catalogue/models';
import { LogsService } from '@shared/services/logs';
import { isEmailValid, normalizeAndHash } from '@shared/utils';
import { combineLatest, EMPTY, of, switchMap, take } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Component({
  selector: 'app-formulaire-catalogue',
  templateUrl: './formulaire-catalogue.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ImageComponent,
    ButtonComponent,
    TitleComponent,
    TranslatePipe,
    CheckboxComponent,
    HandleHtmlLinksDirective,
    NgTemplateOutlet,
    ViewportObserverDirective,
  ],
})
export class FormulaireCatalogueComponent extends AbstractDynamicComponent<FormulaireCatalogue> {
  readonly anchor = viewChild<ElementRef<HTMLDivElement>>('anchor');
  readonly email = signal<string>('');
  readonly isFormSubmitted = signal(false);
  readonly isSuccess = signal(false);
  readonly isFormValid: Signal<FormValid> = computed(() => this.validateForm());
  readonly isApiError = signal(false);
  readonly isSending = signal(false);
  readonly isInputVisible = signal(false);
  optin = signal(false);
  private readonly emailSha256: Signal<string> = toSignal(
    combineLatest([
      toObservable(this.email), // reacts to email changes
      toObservable(this.isFormValid), // reacts to form validity changes
    ]).pipe(
      switchMap(([email, form]) => {
        if (form.status !== 'OK' || !email) {
          return of('');
        }
        return normalizeAndHash(email).pipe(
          catchError((err: unknown) => {
            this.logs.warn('FormulaireCatalogueComponent', 'Failed to hash email for GTM enhanced conversions', err);
            return of('');
          })
        );
      })
    ),
    { initialValue: '' }
  );
  private readonly catalogueService = inject(CatalogueService);
  private readonly logs = inject(LogsService);

  onInputChange($event: Event): void {
    const target = $event.target as HTMLInputElement;
    const { id, value, checked } = target;
    this.logs.trace('FormulaireCatalogueComponent', `onInputChange - ${id}`);
    switch (id) {
      case 'email':
        this.email.set(value);
        break;
      case 'optin':
        this.optin.set(checked);
        break;
    }
  }

  onSubmit(buttonLabel: string): void {
    if (this.isSending()) return;
    this.logs.debug('FormulaireCatalogueComponent', 'onSubmit', this.isFormValid());
    this.isApiError.set(false);
    this.isFormSubmitted.set(true);
    if (this.isFormValid().status === 'KO') {
      this.scrollToFormStart();
    } else {
      this.isSending.set(true);
      const toSaved: Omit<Catalogue, 'id'> = {
        email: this.email(),
        consentementEmail: this.optin(),
        consentementSms: false,
      };
      this.catalogueService
        .create(toSaved, true)
        .pipe(
          take(1),
          tap(() => {
            this.isSuccess.set(true);
            this.logs.info('FormulaireCatalogueComponent', 'Catalogue request has been successfully completed');
            const gtmDownload = this.data.gtmConfigDownloadBtn;
            this.emitGtmEvent({
              event: gtmDownload?.event,
              group: gtmDownload?.group,
              description: gtmDownload?.description,
              context: gtmDownload?.context,
              button_text_value: buttonLabel,
              event_type: 'submit',
              user_email: this.emailSha256(),
            });
          }),
          catchError((e) => {
            this.logs.error(
              'FormulaireCatalogueComponent',
              'An error occurred while requesting the sending of the catalogue',
              e
            );
            this.isApiError.set(true);
            this.isSending.set(false);
            return EMPTY;
          })
        )
        .subscribe();
    }
  }

  private validateForm() {
    const errors: FormValid['errors'] = {};

    if (this.email().length === 0) {
      errors.email = 'GLOBAL.FORMS.EMAIL_REQUIRED_ERROR';
    } else if (!isEmailValid(this.email())) {
      errors.email = 'GLOBAL.FORMS.EMAIL_INVALID_ERROR';
    }

    return (Object.keys(errors).length > 0 ? { status: 'KO', errors } : { status: 'OK' }) as FormValid;
  }

  private scrollToFormStart(): void {
    const elRef = this.anchor();
    if (!elRef || this.isInputVisible()) return;
    elRef.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
}
